import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_ALL_STAGES_FOR_LEAD_CATEGORY,
  GET_ALL_STATUS_FOR_LEAD_CATEGORY,
  UPDATE_LEAD_STATUS,
  UPDATE_LEAD_STAGE,
} from "../services/userApi";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import { CheckCircle, Lock, Circle } from "@mui/icons-material";
import "../Home/global.css";

const StagesStatusSection = ({
  leadId,
  leadCategoryId,
  stage,
  status,
  onUpdate,
}) => {
  const [selectedStage, setSelectedStage] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [dateTimeDialogOpen, setDateTimeDialogOpen] = useState(false);
  const [pendingStage, setPendingStage] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [activityDateTime, setActivityDateTime] = useState("");
  const [dialogType, setDialogType] = useState(""); // "stage" or "status"

  const {
    data: stagesData,
    loading: stagesLoading,
    error: stagesError,
  } = useQuery(GET_ALL_STAGES_FOR_LEAD_CATEGORY, {
    variables: { leadCategoryId },
    skip: !leadCategoryId,
  });

  const {
    data: statusesData,
    loading: statusesLoading,
    error: statusesError,
  } = useQuery(GET_ALL_STATUS_FOR_LEAD_CATEGORY, {
    variables: { leadCategoryId },
    skip: !leadCategoryId,
  });

  const [updateStage, { loading: updatingStage }] = useMutation(UPDATE_LEAD_STAGE, {
    onCompleted: async (data) => {
      if (data.updateLeadStage.success) {
        try {
          setSelectedStatus(null);
          if (onUpdate) onUpdate();
        } catch (error) {
          console.error("Error clearing status:", error);
        }
      }
    },
    onError: (error) => {
      console.error("Error updating stage:", error);
    },
  });

  const [updateStatus, { loading: updatingStatus }] = useMutation(UPDATE_LEAD_STATUS, {
    onCompleted: (data) => {
      if (data.updateLeadStatus.success) {
        if (onUpdate) onUpdate();
      }
    },
    onError: (error) => {
      console.error("Error updating status:", error);
    },
  });

  useEffect(() => {
    if (stage && stagesData) {
      const foundStage = stagesData.getAllStagesForLeadCategory.find(
        (s) => s.id === stage.id
      );
      if (foundStage) {
        setSelectedStage(foundStage);
      }
    }
  }, [stage, stagesData]);

  useEffect(() => {
    if (status && statusesData) {
      const foundStatus = statusesData.getAllStatusForLeadCategory.find(
        (s) => s.id === status.id
      );
      if (foundStatus) {
        setSelectedStatus(foundStatus);
      }
    } else {
      setSelectedStatus(null);
    }
  }, [status, statusesData]);

  // Set default date-time to current date-time when dialog opens
  useEffect(() => {
    if (dateTimeDialogOpen) {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      setActivityDateTime(`${year}-${month}-${day}T${hours}:${minutes}`);
    }
  }, [dateTimeDialogOpen]);

  const stages = stagesData?.getAllStagesForLeadCategory || [];
  const statuses = statusesData?.getAllStatusForLeadCategory || [];

  const currentStageIndex = stages.findIndex((s) => s.id === selectedStage?.id);
  const isCurrentStatusWon = selectedStatus?.statusName?.toLowerCase() === "won";

  const handleStageClick = async (clickedStage) => {
    const clickedIndex = stages.findIndex((s) => s.id === clickedStage.id);

    // Prevent backward movement
    if (clickedIndex < currentStageIndex) {
      return;
    }

    if (clickedIndex > currentStageIndex) {
      if (clickedIndex !== currentStageIndex + 1) {
        return;
      }
      
      if (!isCurrentStatusWon) {
        return;
      }
    }

    if (selectedStage?.id === clickedStage.id) return;

    // Open dialog for date-time selection
    setPendingStage(clickedStage);
    setPendingStatus(null);
    setDialogType("stage");
    setDateTimeDialogOpen(true);
  };

  const handleStatusClick = async (clickedStatus) => {
    if (selectedStatus?.id === clickedStatus.id) return;

    // Open dialog for date-time selection
    setPendingStatus(clickedStatus);
    setPendingStage(null);
    setDialogType("status");
    setDateTimeDialogOpen(true);
  };

  const handleDateTimeConfirm = async () => {
    if (!activityDateTime) return;

    try {
      // Convert local datetime to UTC
      const localDate = new Date(activityDateTime);
      const utcTimestamp = localDate.toISOString();

      if (dialogType === "stage" && pendingStage) {
        await updateStage({
          variables: { 
            leadId, 
            stageId: pendingStage.id,
            activityTimestamp: utcTimestamp
          },
        });
        setSelectedStage(pendingStage);
        setSelectedStatus(null);
      } else if (dialogType === "status" && pendingStatus) {
        await updateStatus({
          variables: { 
            leadId, 
            statusId: pendingStatus.id,
            activityTimestamp: utcTimestamp
          },
        });
        setSelectedStatus(pendingStatus);
      }

      setDateTimeDialogOpen(false);
      setPendingStage(null);
      setPendingStatus(null);
      setActivityDateTime("");
      setDialogType("");
    } catch (error) {
      console.error(`${dialogType} update failed:`, error);
    }
  };

  const handleDateTimeCancel = () => {
    setDateTimeDialogOpen(false);
    setPendingStage(null);
    setPendingStatus(null);
    setActivityDateTime("");
    setDialogType("");
  };

  const convertHexToRgba = (hex, alpha = 1) => {
    if (!hex) return `rgba(193, 35, 40, ${alpha})`;
    
    hex = hex.replace('#', '');
    
    if (hex.length === 8) {
      const r = parseInt(hex.substring(2, 4), 16);
      const g = parseInt(hex.substring(4, 6), 16);
      const b = parseInt(hex.substring(6, 8), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    
    if (hex.length === 6) {
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    
    return `rgba(193, 35, 40, ${alpha})`;
  };

  if (stagesLoading || statusesLoading) {
    return (
      <Box
        sx={{
          bgcolor: "white",
          borderRadius: 2,
          p: 2.5,
          border: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 140,
        }}
      >
        <CircularProgress size={28} sx={{ color: "#c12328" }} />
      </Box>
    );
  }

  if (stagesError || statusesError) {
    return (
      <Box sx={{ bgcolor: "white", borderRadius: 2, p: 2.5, border: "1px solid #e0e0e0" }}>
        <Typography color="error" fontSize="0.85rem">
          Error loading stages or statuses. Please refresh.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          bgcolor: "white",
          borderRadius: 2,
          p: 2.5,
          border: "1px solid #e0e0e0",
          position: "relative",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          overflow: "hidden",
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(90deg, #c12328, #ff6b6b, #c12328)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 3s ease-in-out infinite',
          },
          '@keyframes shimmer': {
            '0%': { backgroundPosition: '200% 0' },
            '100%': { backgroundPosition: '-200% 0' },
          },
        }}
      >
        {/* Stages Section */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, fontWeight: 700, color: "#333", fontSize: "0.8rem", letterSpacing: '0.5px', textTransform: 'uppercase' }}
          >
            Stages
          </Typography>

          <Stack
            direction="row"
            spacing={0}
            sx={{
              alignItems: "center",
              flexWrap: "nowrap",
              overflowX: "auto",
              pb: 1,
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {stages.map((stageItem, index) => {
              const isCompleted = index < currentStageIndex;
              const isCurrent = index === currentStageIndex;
              const isFuture = index > currentStageIndex;
              const isBackward = index < currentStageIndex;
              const canClick = !isBackward && (index === currentStageIndex || (index === currentStageIndex + 1 && isCurrentStatusWon));

              const stageColor = stageItem.stageColor 
                ? convertHexToRgba(stageItem.stageColor, 1)
                : "#c12328";

              let bgColor, textColor, shadowColor;

              if (isCompleted) {
                bgColor = "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)";
                textColor = "white";
                shadowColor = "rgba(76, 175, 80, 0.3)";
              } else if (isCurrent) {
                bgColor = `linear-gradient(135deg, ${stageColor} 0%, ${convertHexToRgba(stageItem.stageColor || "#c12328", 0.85)} 100%)`;
                textColor = "white";
                shadowColor = convertHexToRgba(stageItem.stageColor || "#c12328", 0.3);
              } else {
                bgColor = "#f5f5f5";
                textColor = "#999";
                shadowColor = "rgba(0,0,0,0.05)";
              }

              return (
                <Box
                  key={stageItem.id}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Box
                    onClick={() =>
                      !updatingStage &&
                      canClick &&
                      handleStageClick(stageItem)
                    }
                    sx={{
                      position: 'relative',
                      px: 2,
                      py: 0.75,
                      borderRadius: "20px",
                      background: bgColor,
                      color: textColor,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "capitalize",
                      cursor: updatingStage || !canClick ? "not-allowed" : "pointer",
                      opacity: !canClick ? 0.5 : 1,
                      transition: "all 0.25s ease",
                      minWidth: 90,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.6,
                      boxShadow: `0 2px 8px ${shadowColor}`,
                      transform: isCurrent ? 'scale(1.05)' : 'scale(1)',
                      "&:hover": updatingStage || !canClick
                        ? {}
                        : { 
                            transform: 'scale(1.08) translateY(-1px)',
                            boxShadow: `0 4px 12px ${shadowColor}`,
                          },
                      '&::after': isCurrent ? {
                        content: '""',
                        position: 'absolute',
                        inset: -1,
                        borderRadius: '21px',
                        padding: '1px',
                        background: `linear-gradient(135deg, ${stageColor}, transparent)`,
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                        opacity: 0.5,
                        animation: 'pulse 2s ease-in-out infinite',
                      } : {},
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 0.5 },
                        '50%': { opacity: 1 },
                      },
                    }}
                  >
                    {isCompleted && <CheckCircle sx={{ fontSize: 14 }} />}
                    {isFuture && !canClick && <Lock sx={{ fontSize: 12, opacity: 0.7 }} />}
                    {stageItem.stageName}
                  </Box>

                  {index < stages.length - 1 && (
                    <Box
                      sx={{
                        width: 0,
                        height: 0,
                        borderTop: "10px solid transparent",
                        borderBottom: "10px solid transparent",
                        borderLeft: `10px solid ${
                          isCompleted
                            ? "#66bb6a"
                            : isCurrent
                            ? stageColor
                            : "#e0e0e0"
                        }`,
                        mx: -0.3,
                        transition: 'all 0.25s ease',
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>

        {/* Status Section */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ mb: 1.5, fontWeight: 700, color: "#333", fontSize: "0.8rem", letterSpacing: '0.5px', textTransform: 'uppercase' }}
          >
            Status
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            {statuses.map((statusItem) => {
              const isSelected = selectedStatus?.id === statusItem.id;
              const statusColor = statusItem.statusColor 
                ? convertHexToRgba(statusItem.statusColor, 1)
                : (statusItem.statusName.toLowerCase() === "won" ? "#4caf50" : "#1976d2");

              return (
                <Chip
                  key={statusItem.id}
                  label={statusItem.statusName}
                  onClick={() => !updatingStatus && handleStatusClick(statusItem)}
                  icon={isSelected ? <Circle sx={{ fontSize: 8 }} /> : undefined}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    height: 28,
                    borderRadius: "14px",
                    background: isSelected 
                      ? `linear-gradient(135deg, ${statusColor} 0%, ${convertHexToRgba(statusItem.statusColor || (statusItem.statusName.toLowerCase() === "won" ? "#4caf50" : "#1976d2"), 0.85)} 100%)`
                      : "white",
                    color: isSelected ? "white" : "#555",
                    border: `1.5px solid ${isSelected ? statusColor : "#ddd"}`,
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    textTransform: "capitalize",
                    cursor: updatingStatus ? "not-allowed" : "pointer",
                    opacity: updatingStatus ? 0.6 : 1,
                    transition: "all 0.25s ease",
                    boxShadow: isSelected 
                      ? `0 2px 8px ${convertHexToRgba(statusItem.statusColor || (statusItem.statusName.toLowerCase() === "won" ? "#4caf50" : "#1976d2"), 0.3)}`
                      : "0 1px 4px rgba(0,0,0,0.06)",
                    transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                    "&:hover": updatingStatus
                      ? {}
                      : {
                          transform: 'scale(1.08) translateY(-1px)',
                          boxShadow: isSelected
                            ? `0 4px 12px ${convertHexToRgba(statusItem.statusColor || (statusItem.statusName.toLowerCase() === "won" ? "#4caf50" : "#1976d2"), 0.4)}`
                            : "0 2px 8px rgba(0,0,0,0.12)",
                          borderColor: statusColor,
                        },
                    '& .MuiChip-icon': {
                      marginLeft: '6px',
                      marginRight: '-4px',
                    }
                  }}
                />
              );
            })}
          </Stack>
        </Box>

        {/* Loading Overlay */}
        {(updatingStage || updatingStatus) && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(2px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              zIndex: 10,
            }}
          >
            <CircularProgress size={28} thickness={4.5} sx={{ color: "#c12328" }} />
          </Box>
        )}
      </Box>

      {/* Date-Time Selection Dialog */}
      <Dialog 
        open={dateTimeDialogOpen} 
        onClose={handleDateTimeCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#333" }}>
          Select Activity Date & Time
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
            Choose the date and time for this {dialogType === "stage" ? "stage movement" : "status change"}:
          </Typography>
          <TextField
            type="datetime-local"
            fullWidth
            value={activityDateTime}
            onChange={(e) => setActivityDateTime(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleDateTimeCancel}
            sx={{ 
              color: "#666",
              textTransform: "none",
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDateTimeConfirm}
            variant="contained"
            disabled={!activityDateTime}
            sx={{ 
              bgcolor: "#c12328",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                bgcolor: "#a01d22"
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StagesStatusSection;